import React from 'react';
import { useTranslation } from 'react-i18next';

/** Shared building blocks of the editor's right panel, so every panel looks the same. */

const MIN_LENGTH = 0.5;

export function PanelHeader({
  icon,
  title,
  subtitle,
  action,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle?: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <header className="ve-props-head">
      <span className="ve-props-head-icon" aria-hidden>{icon}</span>
      <div className="ve-props-head-text">
        <h2 className="ve-props-title">{title}</h2>
        {subtitle && <p className="ve-props-head-sub">{subtitle}</p>}
      </div>
      {action}
    </header>
  );
}

export function PanelSectionTitle({ children }: { children: React.ReactNode }) {
  return <h3 className="ve-props-section-title">{children}</h3>;
}

/** Start / end of an item in seconds (kept inside the video and at least 0.5 s long). */
export function TimingFields({
  start,
  end,
  duration,
  onChange,
  onCommit,
}: {
  start: number;
  end: number;
  duration: number;
  onChange: (start: number, end: number) => void;
  onCommit?: () => void;
}) {
  const { t } = useTranslation();
  const max = Number.isFinite(duration) && duration > 0 ? duration : end;
  const round = (n: number) => Math.round(n * 10) / 10;

  return (
    <div className="ve-props-canvas-grid">
      <label className="ve-props-mini-label">
        {t('video_editor_panel.start', 'Start (s)')}
        <SecondsInput
          value={start}
          onApply={(v) => { onChange(Math.max(0, Math.min(v, end - MIN_LENGTH)), end); onCommit?.(); }}
        />
      </label>
      <label className="ve-props-mini-label">
        {t('video_editor_panel.end', 'End (s)')}
        <SecondsInput
          value={end}
          onApply={(v) => { onChange(start, Math.min(max, Math.max(v, start + MIN_LENGTH))); onCommit?.(); }}
        />
      </label>
      <p className="ve-props-hint ve-props-grid-full">
        {t('video_editor_panel.timing_hint', 'Shown for {{length}} s. You can also drag its block on the timeline.', {
          length: round(end - start).toFixed(1),
        })}
      </p>
    </div>
  );
}

/** Number field that applies on Enter or when leaving it, so typing isn't corrected mid-way. */
function SecondsInput({ value, onApply }: { value: number; onApply: (seconds: number) => void }) {
  const shown = String(Math.round(value * 10) / 10);
  const [draft, setDraft] = React.useState<string | null>(null);
  const apply = () => {
    const v = Number(draft);
    if (draft !== null && draft.trim() !== '' && Number.isFinite(v)) onApply(v);
    setDraft(null);
  };
  return (
    <input
      type="number"
      className="ve-props-num"
      step={0.1}
      min={0}
      value={draft ?? shown}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={apply}
      onKeyDown={(e) => {
        if (e.key === 'Enter') apply();
        if (e.key === 'Escape') setDraft(null);
      }}
    />
  );
}
